import React from "react";
import { DocumentIcon } from "@heroicons/react/24/outline";

interface FilePreviewProps {
  className?: string;
  name: string;
  type: string;
  url: string;
}

const FilePreview: React.FC<FilePreviewProps> = React.memo(
  ({
    name,
    type,
    url,
    className = "w-full h-full object-contain rounded-lg",
  }) => {
    const fileType = type?.toLowerCase();

    if (fileType?.includes("video")) {
      return <video src={url} controls className={className} />;
    }

    if (fileType?.includes("image")) {
      return <img src={url} alt={name} className={className} />;
    }

    if (
      fileType === "application/pdf" ||
      fileType?.includes("pdf") ||
      name?.toLowerCase().endsWith(".pdf")
    ) {
      return <embed src={url} className={className} title={name} />;
    }

    // Handle Word documents (.doc, .docx)
    const isWordDoc =
      fileType?.includes("application/msword") ||
      fileType?.includes(
        "application/vnd.openxmlformats-officedocument.wordprocessingml"
      ) ||
      name?.toLowerCase().endsWith(".doc") ||
      name?.toLowerCase().endsWith(".docx");

    if (isWordDoc) {
      const googleDocsViewer = `https://docs.google.com/viewer?url=${encodeURIComponent(
        url
      )}&embedded=true`;

      return (
        <div className="w-full flex flex-col">
          <iframe
            src={googleDocsViewer}
            className="w-full flex-1 border rounded-lg"
            title={name}
            allowFullScreen
          />
          <div className="mt-2 text-center flex gap-4 justify-center">
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-default hover:text-primary-dark text-sm underline"
            >
              Open in new tab
            </a>
            <a
              href={url}
              download={name}
              className="text-primary-default hover:text-primary-dark text-sm underline"
            >
              Download
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full">
        <DocumentIcon className="h-20 w-20 text-secondary-400" />
        <span className="mt-2 text-gray-600">Preview not available</span>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 text-primary-default hover:text-primary-dark text-sm underline"
          >
            Download file
          </a>
        )}
      </div>
    );
  }
);

FilePreview.displayName = "FilePreview";

export default FilePreview;
